import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Gift } from "lucide-react";
import {
  clubErrorMessage,
  getPublicMenuInfo,
  requestClubJoinCode,
  verifyClubJoinCode,
} from "../../api/club";
import BirthdayField, { isBirthdayComplete } from "../../components/customer-club/BirthdayField";
import { normalizePersianDigits, formatPersianNumber } from "../../utils/persianNumber";
import { assetUrl } from "../../hooks/useCustomerMenu";

const RESEND_SECONDS = 60;
const inputClass =
  "h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10";

export const clubJoinedKey = (menuLink) => `clubJoined_${menuLink}`;

// Public page where a guest joins a restaurant's customer club:
// details → SMS code → done.
export default function ClubJoinPage() {
  const { menuLink } = useParams();
  const [info, setInfo] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [step, setStep] = useState("details");
  const [form, setForm] = useState({ name: "", phone: "", birthday: "", marketingConsent: true });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getPublicMenuInfo(menuLink)
      .then((data) => {
        setInfo(data);
        setLoadState("ready");
      })
      .catch(() => setLoadState("error"));
  }, [menuLink]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const restaurant = info?.restaurant;
  const phone = normalizePersianDigits(form.phone).replace(/\D/g, "");

  const sendCode = async (event) => {
    event?.preventDefault();
    if (!/^09\d{9}$/.test(phone)) return setError("شماره موبایل را به شکل ۰۹xxxxxxxxx وارد کنید.");
    if (!isBirthdayComplete(form.birthday)) return setError("روز و ماه تولد را کامل انتخاب کنید یا خالی بگذارید.");
    setBusy(true);
    setError("");
    try {
      await requestClubJoinCode(restaurant._id, phone);
      setStep("code");
      setResendIn(RESEND_SECONDS);
    } catch (sendError) {
      setError(clubErrorMessage(sendError, "ارسال کد ناموفق بود. دوباره تلاش کنید."));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event) => {
    event.preventDefault();
    const cleanCode = normalizePersianDigits(code).replace(/\D/g, "");
    if (cleanCode.length < 6) return setError("کد ۶ رقمی را وارد کنید.");
    setBusy(true);
    setError("");
    try {
      const data = await verifyClubJoinCode(restaurant._id, { ...form, phone, code: cleanCode });
      setResult(data);
      setStep("done");
      try {
        localStorage.setItem(clubJoinedKey(menuLink), "1");
      } catch {
        // storage unavailable: the prompt may show again, which is harmless
      }
    } catch (verifyError) {
      setError(clubErrorMessage(verifyError, "تأیید کد ناموفق بود."));
    } finally {
      setBusy(false);
    }
  };

  const logo = restaurant
    ? assetUrl(restaurant.restaurantSettings?.profileImage || restaurant.logo, restaurant._id, "/images/default-profile.png")
    : null;

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <header className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <Link to={`/menu/${menuLink}`} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100" aria-label="بازگشت به منو">
          <ArrowRight size={20} aria-hidden="true" />
        </Link>
        {restaurant && (
          <>
            <img src={logo} alt="" className="h-9 w-9 rounded-full border border-gray-200 object-cover" />
            <span className="truncate font-bold text-gray-800">{restaurant.name}</span>
          </>
        )}
      </header>

      <main className="mx-auto max-w-md p-4 pt-8">
        {loadState === "loading" && <div className="h-72 animate-pulse rounded-2xl bg-gray-200" aria-busy="true" />}

        {loadState === "error" && (
          <p className="rounded-2xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">اطلاعات رستوران پیدا نشد.</p>
        )}

        {loadState === "ready" && !info.customerClubEnabled && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center">
            <p className="font-bold text-gray-800">این رستوران هنوز باشگاه مشتریان ندارد.</p>
            <Link to={`/menu/${menuLink}`} className="mt-4 inline-block text-sm font-bold text-primary hover:underline">بازگشت به منو</Link>
          </div>
        )}

        {loadState === "ready" && info.customerClubEnabled && (
          <section className="rounded-2xl border border-gray-200 bg-white p-5">
            {step === "done" ? (
              <div className="py-4 text-center">
                <CheckCircle2 size={40} className="mx-auto text-secondary" aria-hidden="true" />
                <h1 className="mt-3 text-lg font-extrabold text-gray-900">{result?.message || "عضویت شما ثبت شد."}</h1>
                <p className="mt-2 text-sm leading-6 text-gray-500">
                  از این به بعد {restaurant.name} شما را می‌شناسد{form.marketingConsent ? " و پیشنهادهای ویژه را برایتان پیامک می‌کند" : ""}.
                </p>
                <Link
                  to={`/menu/${menuLink}`}
                  className="mt-5 inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-6 text-sm font-bold text-white hover:bg-[#e8731f]"
                >
                  بازگشت به منو
                </Link>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary_1 text-primary">
                    <Gift size={20} aria-hidden="true" />
                  </span>
                  <div>
                    <h1 className="text-lg font-extrabold text-gray-900">عضویت در باشگاه مشتریان</h1>
                    <p className="mt-1 text-sm leading-6 text-gray-500">
                      با عضویت در باشگاه {restaurant.name}، از پیشنهادها و هدیه‌های ویژه باخبر شوید.
                    </p>
                  </div>
                </div>

                {step === "details" ? (
                  <form onSubmit={sendCode} className="mt-5 space-y-3">
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-gray-600">شماره موبایل</span>
                      <input
                        autoFocus
                        inputMode="tel"
                        autoComplete="tel"
                        dir="ltr"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="09xxxxxxxxx"
                        className={`${inputClass} text-right`}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-semibold text-gray-600">نام (اختیاری)</span>
                      <input
                        autoComplete="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        maxLength={120}
                        className={inputClass}
                      />
                    </label>
                    <div>
                      <span className="mb-1 block text-xs font-semibold text-gray-600">تاریخ تولد (اختیاری، برای هدیه تولد)</span>
                      <BirthdayField value={form.birthday} onChange={(birthday) => setForm({ ...form, birthday })} idPrefix="join-birthday" />
                    </div>
                    <label className="flex items-start gap-2 pt-1 text-sm leading-6 text-gray-700">
                      <input
                        type="checkbox"
                        checked={form.marketingConsent}
                        onChange={(e) => setForm({ ...form, marketingConsent: e.target.checked })}
                        className="mt-1.5 h-4 w-4 accent-primary"
                      />
                      <span>پیشنهادها و تخفیف‌های این رستوران را برایم پیامک کنید. هر زمان می‌توانم لغو کنم.</span>
                    </label>
                    {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700" role="alert">{error}</p>}
                    <button
                      type="submit"
                      disabled={busy}
                      className="min-h-11 w-full rounded-xl bg-primary text-sm font-bold text-white hover:bg-[#e8731f] disabled:opacity-60"
                    >
                      {busy ? "در حال ارسال…" : "دریافت کد تأیید"}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={verify} className="mt-5 space-y-3">
                    <p className="text-sm text-gray-600">
                      کد ۶ رقمی به <span dir="ltr" className="font-bold text-gray-800">{phone}</span> پیامک شد.{" "}
                      <button type="button" onClick={() => { setStep("details"); setCode(""); setError(""); }} className="font-bold text-primary hover:underline">
                        ویرایش شماره
                      </button>
                    </p>
                    <input
                      autoFocus
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      dir="ltr"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="------"
                      aria-label="کد تأیید"
                      className={`${inputClass} text-center text-lg tracking-[0.5em]`}
                    />
                    {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700" role="alert">{error}</p>}
                    <button
                      type="submit"
                      disabled={busy}
                      className="min-h-11 w-full rounded-xl bg-primary text-sm font-bold text-white hover:bg-[#e8731f] disabled:opacity-60"
                    >
                      {busy ? "در حال بررسی…" : "تأیید و عضویت"}
                    </button>
                    <button
                      type="button"
                      onClick={sendCode}
                      disabled={resendIn > 0 || busy}
                      className="w-full text-xs font-bold text-gray-500 hover:text-primary disabled:cursor-default disabled:hover:text-gray-500"
                    >
                      {resendIn > 0 ? `ارسال دوباره کد تا ${formatPersianNumber(resendIn)} ثانیه دیگر` : "ارسال دوباره کد"}
                    </button>
                  </form>
                )}
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
